import React from 'react';
import { LucideIcon } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'danger' | 'outline' | 'success' | 'glass' | 'glass-primary';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md',
  children, 
  className = '',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  isLoading = false,
  disabled,
  ...props 
}) => {
  // Base styles applied to all buttons with improved transitions
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg active:scale-95';
  
  // Variant styles mapping with modern colors and hover effects
  const variantStyles: Record<ButtonVariant, string> = {
    primary: 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 focus:ring-primary-500 shadow-md hover:shadow-xl hover:-translate-y-0.5 dark:from-primary-600 dark:to-primary-700 dark:hover:from-primary-700 dark:hover:to-primary-800',
    secondary: 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-white hover:from-secondary-600 hover:to-secondary-700 focus:ring-secondary-500 shadow-md hover:shadow-xl hover:-translate-y-0.5 dark:from-secondary-600 dark:to-secondary-700 dark:hover:from-secondary-700 dark:hover:to-secondary-800',
    accent: 'bg-gradient-to-r from-accent-500 to-accent-600 text-white hover:from-accent-600 hover:to-accent-700 focus:ring-accent-500 shadow-md hover:shadow-xl hover:-translate-y-0.5 dark:from-accent-600 dark:to-accent-700 dark:hover:from-accent-700 dark:hover:to-accent-800',
    danger: 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 focus:ring-red-500 shadow-md hover:shadow-xl hover:-translate-y-0.5 dark:from-red-600 dark:to-red-700 dark:hover:from-red-700 dark:hover:to-red-800',
    success: 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 focus:ring-green-500 shadow-md hover:shadow-xl hover:-translate-y-0.5 dark:from-green-600 dark:to-green-700 dark:hover:from-green-700 dark:hover:to-green-800',
    outline: 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:ring-gray-300 dark:focus:ring-gray-600 shadow-sm hover:shadow-md',
    glass: 'bg-white/30 dark:bg-gray-800/30 backdrop-blur-md text-gray-900 dark:text-gray-100 border border-white/40 dark:border-gray-700/40 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.2)] dark:hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] focus:ring-primary-500',
    'glass-primary': 'bg-primary-500/20 dark:bg-primary-600/20 backdrop-blur-md text-primary-700 dark:text-primary-300 border border-primary-500/30 dark:border-primary-600/30 hover:bg-primary-500/30 dark:hover:bg-primary-600/30 hover:shadow-[0_8px_32px_0_rgba(59,130,246,0.3)] dark:hover:shadow-[0_8px_32px_0_rgba(59,130,246,0.4)] focus:ring-primary-500',
  };
  
  // Size styles mapping
  const sizeStyles: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };
  
  // Icon size based on button size
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };
  
  // Combine all classes
  const buttonClasses = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;
  
  return (
    <button className={buttonClasses} disabled={disabled || isLoading} {...props}>
      {isLoading ? (
        <svg className={`animate-spin ${iconSizes[size]}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : LeftIcon && (
        <LeftIcon className={iconSizes[size]} />
      )}
      {children}
      {!isLoading && RightIcon && <RightIcon className={iconSizes[size]} />}
    </button>
  );
};

export default Button;
